import React, { useCallback, useState } from 'react';

import { Popconfirm, Spin } from 'antd-v5';
import cn from 'classnames';
import { MdDelete, MdOutlineClose } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button } from 'scouting-ui/lib';

import { Card } from '@shared/components';

import styles from './Banner.styles.less';
import { useEventsCommunications } from './hooks';

function Banner() {
  const { announcements, deleteEvent, loading } = useEventsCommunications();

  const [hiddenBanners, setHiddenBanners] = useState<string[]>([]);
  const [leavingBanners, setLeavingBanners] = useState<string[]>([]);

  const handleHide = useCallback((eventGuid: string) => {
    setLeavingBanners(prev => [...prev, eventGuid]);
  }, []);

  const handleAnimationEnd = useCallback(
    (eventGuid: string) => (event: React.AnimationEvent<HTMLElement>) => {
      if (!event.animationName.includes('flyOut')) {
        return;
      }

      setHiddenBanners(prev =>
        prev.includes(eventGuid) ? prev : [...prev, eventGuid],
      );
    },
    [],
  );

  return (
    <div className={styles.bannerContainer} id="BannerContainer">
      {loading && <Spin spinning={loading} />}
      {announcements
        .filter(({ eventGuid }) => !hiddenBanners.includes(eventGuid))
        .map(announcement => {
          const { announcementMessage, announcementTitle, eventGuid } =
            announcement;
          const editAllowed = announcement.own;

          return (
            <Card
              className={cn(
                styles.banner,
                leavingBanners.includes(eventGuid) && styles.leaving,
              )}
              key={eventGuid}
              noPadding
              onAnimationEnd={handleAnimationEnd(eventGuid)}
              shadow
            >
              <header>
                <h2>{announcementTitle}</h2>
                <div className={styles.actions}>
                  {editAllowed && (
                    <Popconfirm
                      cancelButtonProps={{ type: 'default' }}
                      cancelText={<FormattedMessage id="shared.no" />}
                      description={
                        <FormattedMessage id="core.banner.removeDescription" />
                      }
                      okButtonProps={{ danger: true }}
                      okText={<FormattedMessage id="shared.remove" />}
                      onConfirm={() => deleteEvent(eventGuid)}
                      title={<FormattedMessage id="core.banner.removeTitle" />}
                    >
                      <Button
                        data-testid="remove-banner-button"
                        icon={<MdDelete />}
                        size="small"
                        type="primary"
                      >
                        <FormattedMessage id="shared.remove" />
                      </Button>
                    </Popconfirm>
                  )}
                  {!editAllowed && (
                    <Button
                      data-testid="hide-banner-button"
                      icon={<MdOutlineClose />}
                      onClick={() => handleHide(eventGuid)}
                      size="small"
                      type="primary"
                    >
                      <FormattedMessage id="core.banner.dismiss" />
                    </Button>
                  )}
                </div>
              </header>
              <article
                dangerouslySetInnerHTML={{ __html: announcementMessage }}
              />
            </Card>
          );
        })}
    </div>
  );
}

export default Banner;
